import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, ComposedChart, Area } from 'recharts';

interface ROIBusinessImpactAnalysisProps {
  data: {
    investment_flow: {
      total_investment: number;
      training_costs: number;
      mentor_costs: number;
      infrastructure_costs: number;
      operational_costs: number;
    };
    business_impact: {
      direct_savings: number;
      indirect_savings: number;
      productivity_gains: number;
      quality_improvements: number;
      innovation_value: number;
      total_business_value: number;
    };
    roi_metrics: {
      roi_percentage: number;
      payback_period_months: number;
      net_present_value: number;
      cost_benefit_ratio: number;
      break_even_point: string;
    };
    department_roi: Array<{
      department: string;
      investment: number;
      returns: number;
      roi_percentage: number;
      payback_period: number;
    }>;
    timeline_analysis: Array<{
      month: string;
      cumulative_investment: number;
      cumulative_returns: number;
      net_value: number;
      roi_trend: number;
    }>;
    value_attribution: Array<{
      category: string;
      value: number;
      percentage: number;
      description: string;
    }>;
  };
  loading?: boolean;
}

const ROIBusinessImpactAnalysis: React.FC<ROIBusinessImpactAnalysisProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ROI & Business Impact Analysis</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ROI & Business Impact Analysis</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No ROI data available
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getROILevel = (roi: number) => {
    if (roi >= 300) return { level: 'Exceptional', color: '#10b981' };
    if (roi >= 200) return { level: 'Excellent', color: '#3b82f6' };
    if (roi >= 100) return { level: 'Good', color: '#8b5cf6' };
    if (roi >= 50) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Below Target', color: '#ef4444' };
  };

  const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">ROI & Business Impact Analysis</h3>
      
      {/* ROI Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">ROI Percentage</p>
              <p className="text-2xl font-bold text-green-900">{formatPercentage(data.roi_metrics.roi_percentage)}</p>
            </div>
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-sm">📈</span>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Payback Period</p>
              <p className="text-2xl font-bold text-blue-900">{data.roi_metrics.payback_period_months} months</p>
            </div>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm">⏱️</span>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Net Present Value</p>
              <p className="text-2xl font-bold text-purple-900">{formatCurrency(data.roi_metrics.net_present_value)}</p>
            </div>
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 text-sm">💰</span>
            </div>
          </div>
        </div>
        
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Cost-Benefit Ratio</p>
              <p className="text-2xl font-bold text-orange-900">{(data.roi_metrics.cost_benefit_ratio || 0).toFixed(2)}:1</p>
            </div>
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 text-sm">⚖️</span>
            </div>
          </div>
        </div>
      </div>

      {/* Investment vs Returns Timeline */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Investment vs Returns Timeline</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.timeline_analysis} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })}
              />
              <YAxis 
                yAxisId="left"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => formatCurrency(value)}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value || 0).toFixed(1)}%`}
              />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'cumulative_investment') return [formatCurrency(value), 'Cumulative Investment'];
                  if (name === 'cumulative_returns') return [formatCurrency(value), 'Cumulative Returns'];
                  if (name === 'net_value') return [formatCurrency(value), 'Net Value'];
                  if (name === 'roi_trend') return [`${(value || 0).toFixed(1)}%`, 'ROI Trend'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Month: ${new Date(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="cumulative_investment"
                stackId="1"
                stroke="#ef4444"
                fill="#ef4444"
                fillOpacity={0.3}
                name="Cumulative Investment"
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="cumulative_returns"
                stackId="1"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.3}
                name="Cumulative Returns"
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="net_value"
                stroke="#3b82f6"
                strokeWidth={3}
                name="Net Value"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="roi_trend"
                stroke="#8b5cf6"
                strokeWidth={3}
                strokeDasharray="5 5"
                name="ROI Trend"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Department ROI Comparison */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Department ROI Comparison</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.department_roi} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="department" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                yAxisId="left"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => formatCurrency(value)}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value || 0).toFixed(1)}%`}
              />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'investment') return [formatCurrency(value), 'Investment'];
                  if (name === 'returns') return [formatCurrency(value), 'Returns'];
                  if (name === 'roi_percentage') return [`${(value || 0).toFixed(1)}%`, 'ROI %'];
                  return [value, name];
                }}
              />
              <Bar yAxisId="left" dataKey="investment" fill="#ef4444" name="Investment" />
              <Bar yAxisId="left" dataKey="returns" fill="#10b981" name="Returns" />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="roi_percentage"
                stroke="#8b5cf6"
                strokeWidth={3}
                name="ROI %"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Value Attribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Business Value Attribution</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.value_attribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name}: ${(((percent as number) || 0) * 100).toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {data.value_attribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => [formatCurrency(value), 'Value']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Investment Breakdown</h4>
          <div className="space-y-3">
            {Object.entries(data.investment_flow).map(([key, value]) => {
              if (key === 'total_investment') return null;
              const percentage = (value / data.investment_flow.total_investment) * 100;
              const label = key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
              return (
                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="h-2 rounded-full bg-blue-500" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(value)} ({(percentage || 0).toFixed(1)}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-green-800">Total Business Value</span>
              <span className="text-lg font-bold text-green-900">
                {formatCurrency(data.business_impact.total_business_value)}
              </span>
            </div>
            <div className="text-xs text-green-600 mt-1">
              ROI Level: {getROILevel(data.roi_metrics.roi_percentage).level}
            </div>
          </div>
        </div>
      </div>

      {/* Business Impact Summary */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-3">Business Impact Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-900">{formatCurrency(data.business_impact.direct_savings)}</div>
            <div className="text-sm text-blue-600">Direct Savings</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-900">{formatCurrency(data.business_impact.productivity_gains)}</div>
            <div className="text-sm text-blue-600">Productivity Gains</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-900">{formatCurrency(data.business_impact.innovation_value)}</div>
            <div className="text-sm text-blue-600">Innovation Value</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROIBusinessImpactAnalysis;

